/*
 * Authors: mariushirjanu
 * Created: 29/07/2022
 */
import { Component, Input, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { GlobalProductService } from '../global-product.service';
import { GlobalProductDataService, IProductImagesHolder } from '../global-product-data.service';
import { GaMessageService } from '../../../shared/message/ga-message.service';
import { GaValidateService } from '../../../shared/formly/ga-validate.service';
import { GlobalProductEditService } from './global-product-edit.service';
import { AuthService } from '../../../shared/services/auth/auth.service';
import { IFileUpload, IFileUploadFailResponse } from '../../../shared/file-upload/file-upload.service';
import { GaEventService, IGAEvent } from '../../../shared/services/ga-event.service';
import { IHTMLOrSVGElement } from '../../../../type_definitions/vendor';
import { EVENT_GLOBAL_PRODUCT_IMAGES_LOADED } from '../../../shared/app-events';
import { IProductImage } from '../../../../type_definitions/datatypes';

@Component({
  selector: 'global-product-edit-images',
  template: `
    <h2 *ngIf="loading">
      <activity-indicator text="Loading images"></activity-indicator>
    </h2>

    <div *ngIf="!loading">
      <h2>Images</h2>

      <p>Images make the catalog look more attractive for your customer.</p>
      <p>
        <file-button [url]="imagePostUrl" [autoUpload]="true" (onFileAdd)="onFileAdd($event)" (onUploadStarted)="onUploadStarted()" (onUploadDone)="onUploadDone($event)" (onUploadFailed)="onUploadFailed($event)" [uploading]="saving">
          <i class="fa fa-folder-open-o"></i> Add Image
        </file-button>&nbsp;
        <button class="btn" (click)="reorderImages()" [busyButton]="reorderBusy" [disabled]="saving">Reorder Images</button>
      </p>
      <div class="form-horizontal images-form clearfix" *ngIf="productImages?.images">
        <div *ngFor="let image of productImages.images; trackById" class="image">
          <img [src]="image.url" alt=""/>
          <div class="clearfix">
            <button #deleteButton class="btn btn-default" (click)="deleteImage(image, deleteButton)" [busyButton]="deleteButton.dataset.busy === 'true'" [disabled]="saving">
              <i class="fa fa-trash-o"></i> Delete
            </button>
          </div>
        </div>
      </div>
      <div class="form-horizontal images-form clearfix" *ngIf="productImages?.modelImages?.length">
        <h4>Model Images</h4>
        <div *ngFor="let image of productImages.modelImages; trackById" class="image">
          <img [src]="image.url" alt=""/>
        </div>
      </div>
    </div>
  `
})
export class GlobalProductEditImagesComponent implements OnInit {
  @Input() public productId:number;

  private eventHandler:Subscription;
  private uploadProgressMessage:Noty;
  private imageToUpload:File;
  public productImages:IProductImagesHolder;
  public loading:boolean = true;
  public saving:boolean;
  public reorderBusy:boolean;
  public imagePostUrl:string;

  constructor(
    private authService:AuthService,
    private validateService:GaValidateService,
    private gaEventService:GaEventService,
    private globalProductDataService:GlobalProductDataService,
    private globalProductService:GlobalProductService,
    private globalProductEditService:GlobalProductEditService,
    private messageService:GaMessageService
  ) {
    /***/
  }

  public ngOnInit():void {
    this.start();
    this.listen();
  }

  private start():void {
    this.productImages = this.globalProductService.images(); // the images are loaded in the parent
    if(this.productImages){
      this.loading = false;
      this.imagePostUrl = this.authService.authenticatedUrl(`rest/globalproduct/products/${this.productId}/images`);
    }
  }

  private listen():void {
    this.eventHandler = this.gaEventService.events.subscribe((event:IGAEvent) => {
      if (event.name === EVENT_GLOBAL_PRODUCT_IMAGES_LOADED) {
        this.start();
      }
    });
  }

  private updateProductImages = (response:IProductImagesHolder) => {
    this.productImages = response;
    this.globalProductService.images(response);
  };

  public onFileAdd(file:File):void {
    this.imageToUpload = file;
  }

  public onUploadStarted():void {
    this.saving = true;
    this.uploadProgressMessage = this.messageService.showStatusMessage(`Uploading image ${this.imageToUpload.name}…`);
  }

  public onUploadDone(result:IFileUpload):void {
    this.saving = false;
    this.messageService.closeMessage(this.uploadProgressMessage);
    this.updateProductImages(<IProductImagesHolder>result.response.data);
  }

  public onUploadFailed(response:IFileUploadFailResponse):void {
    this.saving = false;
    this.messageService.closeMessage(this.uploadProgressMessage);
    if (response) {
      let errorMessage:string = 'Error uploading image';
      if(this.imageToUpload?.name){
        errorMessage += `: ${this.imageToUpload.name}`;
      }
      if (response.status) {
        errorMessage += ` (${response.status}${response.error? ' ' + response.error : ''})`;
      }
      this.messageService.showErrorMessage(errorMessage);
    }
  }

  public reorderImages():void {
    this.reorderBusy = true;
    this.globalProductEditService.showReorderImagesDialog(this.productId)
      .then(this.updateProductImages)
      .finally(() => {
        this.reorderBusy = false;
      });
  }

  public deleteImage(image:IProductImage, button:IHTMLOrSVGElement):void {
    button.dataset.busy = true;
    this.globalProductEditService.showDeleteImageDialog(this.productId, image)
      .then(this.updateProductImages)
      .finally(() => {
        button.dataset.busy = false;
      });
  }
}